import { createReducer, on } from '@ngrx/store';
import { AvatarActions } from './avatar.actions';
import { AvatarState, initialAvatarState } from './models/avatar-state.models';

export const avatarReducer = createReducer(
  initialAvatarState,

  // Load avatars
  on(AvatarActions.loadAvatars, (state): AvatarState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AvatarActions.loadAvatarsSuccess, (state, { response }): AvatarState => ({
    ...state,
    avatars: response.items,
    loading: false,
    error: null,
    cacheTimestamp: Date.now()
  })),
  on(AvatarActions.loadAvatarsFailure, (state, { error }): AvatarState => ({
    ...state,
    loading: false,
    error
  })),

  // Create avatar
  on(AvatarActions.createAvatar, (state): AvatarState => ({
    ...state,
    creatingAvatar: true,
    error: null
  })),
  on(AvatarActions.createAvatarSuccess, (state, { avatar }): AvatarState => ({
    ...state,
    avatars: [...state.avatars, avatar],
    creatingAvatar: false,
    error: null,
    cacheTimestamp: Date.now()
  })),
  on(AvatarActions.createAvatarFailure, (state, { error }): AvatarState => ({
    ...state,
    creatingAvatar: false,
    error
  })),

  // Get avatar status
  on(AvatarActions.getAvatarStatus, (state): AvatarState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AvatarActions.getAvatarStatusSuccess, (state, { avatar }): AvatarState => {
    // Update the avatar in the list if it exists
    const avatarIndex = state.avatars.findIndex(a => a.id === avatar.id);
    const updatedAvatars = avatarIndex >= 0
      ? [...state.avatars.slice(0, avatarIndex), avatar, ...state.avatars.slice(avatarIndex + 1)]
      : [...state.avatars, avatar];

    return {
      ...state,
      avatars: updatedAvatars,
      loading: false,
      error: null,
      cacheTimestamp: Date.now()
    };
  }),
  on(AvatarActions.getAvatarStatusFailure, (state, { error }): AvatarState => ({
    ...state,
    loading: false,
    error
  })),

  // Update avatar
  on(AvatarActions.updateAvatar, (state): AvatarState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AvatarActions.updateAvatarSuccess, (state, { avatar }): AvatarState => {
    const avatarIndex = state.avatars.findIndex(a => a.id === avatar.id);
    const updatedAvatars = avatarIndex >= 0
      ? [...state.avatars.slice(0, avatarIndex), avatar, ...state.avatars.slice(avatarIndex + 1)]
      : state.avatars;

    return {
      ...state,
      avatars: updatedAvatars,
      loading: false,
      error: null,
      cacheTimestamp: Date.now()
    };
  }),
  on(AvatarActions.updateAvatarFailure, (state, { error }): AvatarState => ({
    ...state,
    loading: false,
    error
  })),

  // Delete avatar
  on(AvatarActions.deleteAvatar, (state, { id }): AvatarState => ({
    ...state,
    deletingAvatarId: id,
    error: null
  })),
  on(AvatarActions.deleteAvatarSuccess, (state, { id }): AvatarState => ({
    ...state,
    avatars: state.avatars.filter(a => a.id !== id),
    deletingAvatarId: null,
    error: null,
    cacheTimestamp: Date.now()
  })),
  on(AvatarActions.deleteAvatarFailure, (state, { error }): AvatarState => ({
    ...state,
    deletingAvatarId: null,
    error
  })),

  // Delete all avatars
  on(AvatarActions.deleteAllAvatars, (state): AvatarState => ({
    ...state,
    deletingAllAvatars: true,
    error: null
  })),
  on(AvatarActions.deleteAllAvatarsSuccess, (state): AvatarState => ({
    ...state,
    avatars: [],
    deletingAllAvatars: false,
    error: null,
    cacheTimestamp: Date.now()
  })),
  on(AvatarActions.deleteAllAvatarsFailure, (state, { error }): AvatarState => ({
    ...state,
    deletingAllAvatars: false,
    error
  })),

  // Set active avatar
  // Note: activeAvatar is now derived from preferences.active_avatar_id
  // The optimistic update happens in user-preferences reducer
  // We only track loading state here
  on(AvatarActions.setActiveAvatar, (state): AvatarState => ({
    ...state,
    settingActiveAvatar: true,
    error: null
  })),
  on(AvatarActions.setActiveAvatarSuccess, (state): AvatarState => ({
    ...state,
    settingActiveAvatar: false,
    error: null
  })),
  on(AvatarActions.setActiveAvatarFailure, (state, { error }): AvatarState => ({
    ...state,
    settingActiveAvatar: false,
    error
  })),

  // Remove active avatar
  // Note: activeAvatar is now derived from preferences.active_avatar_id
  // The optimistic update happens in user-preferences reducer
  on(AvatarActions.removeActiveAvatar, (state): AvatarState => ({
    ...state,
    settingActiveAvatar: true,
    error: null
  })),
  on(AvatarActions.removeActiveAvatarSuccess, (state): AvatarState => ({
    ...state,
    settingActiveAvatar: false,
    error: null
  })),
  on(AvatarActions.removeActiveAvatarFailure, (state, { error }): AvatarState => ({
    ...state,
    settingActiveAvatar: false,
    error
  })),

  // Initialize avatars
  on(AvatarActions.initializeAvatars, (state): AvatarState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AvatarActions.initializeAvatarsSuccess, (state): AvatarState => ({
    ...state,
    loading: false,
    error: null
  })),
  on(AvatarActions.initializeAvatarsFailure, (state, { error }): AvatarState => ({
    ...state,
    loading: false,
    error
  })),

  // Clear avatars (on logout)
  on(AvatarActions.clearAvatars, (): AvatarState => ({
    ...initialAvatarState
  })),

  // Invalidate cache
  on(AvatarActions.invalidateCache, (state): AvatarState => ({
    ...state,
    cacheTimestamp: null
  }))
);
